import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type TipoIdentificacion, type Genero, type Grupo, type UnidadMedida, type Procedencia, type OrigenProducto, type Empaque, type ParametricData } from '../types/catalog';
import { CatalogService } from '../services/catalogService';
import { AuthService } from '../auth';

interface ParametricState {
  // Core parametric data
  tiposIdentificacion: TipoIdentificacion[];
  generos: Genero[];
  grupos: Grupo[];

  // Additional parametric data
  unidadesMedida: UnidadMedida[];
  procedencias: Procedencia[];
  origenProductos: OrigenProducto[];
  empaques: Empaque[];

  // Loading states
  loading: boolean;
  loadingTiposIdentificacion: boolean;
  loadingGeneros: boolean;
  loadingGrupos: boolean;
  loadingUnidadesMedida: boolean;
  loadingProcedencias: boolean;
  loadingOrigenProductos: boolean;
  loadingEmpaques: boolean;

  // Error state
  error: string | null;

  // Actions
  fetchAllParametricData: () => Promise<void>;
  fetchTiposIdentificacion: () => Promise<void>;
  fetchGeneros: () => Promise<void>;
  fetchGrupos: () => Promise<void>;
  fetchUnidadesMedida: () => Promise<void>;
  fetchProcedencias: () => Promise<void>;
  fetchOrigenProductos: () => Promise<void>;
  fetchEmpaques: () => Promise<void>;

  // Utility actions
  refetch: () => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  tiposIdentificacion: [],
  generos: [],
  grupos: [],
  unidadesMedida: [],
  procedencias: [],
  origenProductos: [],
  empaques: [],
  loading: false,
  loadingTiposIdentificacion: false,
  loadingGeneros: false,
  loadingGrupos: false,
  loadingUnidadesMedida: false,
  loadingProcedencias: false,
  loadingOrigenProductos: false,
  loadingEmpaques: false,
  error: null,
};

export const useParametricStore = create<ParametricState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchAllParametricData: async () => {
        set({ loading: true, error: null }, false, 'parametric/fetchAll/start');

        try {
          // Only fetch if user is authenticated
          const isAuthenticated = AuthService.isAuthenticated();
          if (!isAuthenticated) {
            throw new Error('User not authenticated');
          }

          // Fetch all parametric data in parallel
          const [
            parametricData,
            unidadesMedida,
            procedencias,
            origenProductos,
            empaques
          ] = await Promise.all([
            CatalogService.getAllParametricData(),
            CatalogService.getUnidadesMedida(),
            CatalogService.getProcedencias(),
            CatalogService.getOrigenProductos(),
            CatalogService.getEmpaques()
          ]);

          set(
            {
              tiposIdentificacion: parametricData.tiposIdentificacion,
              generos: parametricData.generos,
              grupos: parametricData.grupos,
              unidadesMedida,
              procedencias,
              origenProductos,
              empaques,
              loading: false,
            },
            false,
            'parametric/fetchAll/success'
          );
        } catch (error) {
          set(
            {
              loading: false,
              error: error instanceof Error ? error.message : 'Failed to fetch parametric data',
            },
            false,
            'parametric/fetchAll/error'
          );
        }
      },

      fetchTiposIdentificacion: async () => {
        set({ loadingTiposIdentificacion: true, error: null }, false, 'parametric/fetchTiposIdentificacion/start');

        try {
          const tiposIdentificacion = await CatalogService.getTiposIdentificacion();
          set(
            { tiposIdentificacion, loadingTiposIdentificacion: false },
            false,
            'parametric/fetchTiposIdentificacion/success'
          );
        } catch (error) {
          set(
            {
              loadingTiposIdentificacion: false,
              error: error instanceof Error ? error.message : 'Failed to fetch tipos identificacion',
            },
            false,
            'parametric/fetchTiposIdentificacion/error'
          );
        }
      },

      fetchGeneros: async () => {
        set({ loadingGeneros: true, error: null }, false, 'parametric/fetchGeneros/start');

        try {
          const generos = await CatalogService.getGeneros();
          set(
            { generos, loadingGeneros: false },
            false,
            'parametric/fetchGeneros/success'
          );
        } catch (error) {
          set(
            {
              loadingGeneros: false,
              error: error instanceof Error ? error.message : 'Failed to fetch generos',
            },
            false,
            'parametric/fetchGeneros/error'
          );
        }
      },

      fetchGrupos: async () => {
        set({ loadingGrupos: true, error: null }, false, 'parametric/fetchGrupos/start');

        try {
          const grupos = await CatalogService.getGrupos();
          set(
            { grupos, loadingGrupos: false },
            false,
            'parametric/fetchGrupos/success'
          );
        } catch (error) {
          set(
            {
              loadingGrupos: false,
              error: error instanceof Error ? error.message : 'Failed to fetch grupos',
            },
            false,
            'parametric/fetchGrupos/error'
          );
        }
      },

      fetchUnidadesMedida: async () => {
        set({ loadingUnidadesMedida: true, error: null }, false, 'parametric/fetchUnidadesMedida/start');

        try {
          const unidadesMedida = await CatalogService.getUnidadesMedida();
          set(
            { unidadesMedida, loadingUnidadesMedida: false },
            false,
            'parametric/fetchUnidadesMedida/success'
          );
        } catch (error) {
          set(
            {
              loadingUnidadesMedida: false,
              error: error instanceof Error ? error.message : 'Failed to fetch unidades medida',
            },
            false,
            'parametric/fetchUnidadesMedida/error'
          );
        }
      },

      fetchProcedencias: async () => {
        set({ loadingProcedencias: true, error: null }, false, 'parametric/fetchProcedencias/start');

        try {
          const procedencias = await CatalogService.getProcedencias();
          set(
            { procedencias, loadingProcedencias: false },
            false,
            'parametric/fetchProcedencias/success'
          );
        } catch (error) {
          set(
            {
              loadingProcedencias: false,
              error: error instanceof Error ? error.message : 'Failed to fetch procedencias',
            },
            false,
            'parametric/fetchProcedencias/error'
          );
        }
      },

      fetchOrigenProductos: async () => {
        set({ loadingOrigenProductos: true, error: null }, false, 'parametric/fetchOrigenProductos/start');

        try {
          const origenProductos = await CatalogService.getOrigenProductos();
          set(
            { origenProductos, loadingOrigenProductos: false },
            false,
            'parametric/fetchOrigenProductos/success'
          );
        } catch (error) {
          set(
            {
              loadingOrigenProductos: false,
              error: error instanceof Error ? error.message : 'Failed to fetch origen productos',
            },
            false,
            'parametric/fetchOrigenProductos/error'
          );
        }
      },

      fetchEmpaques: async () => {
        set({ loadingEmpaques: true, error: null }, false, 'parametric/fetchEmpaques/start');

        try {
          const empaques = await CatalogService.getEmpaques();
          set(
            { empaques, loadingEmpaques: false },
            false,
            'parametric/fetchEmpaques/success'
          );
        } catch (error) {
          set(
            {
              loadingEmpaques: false,
              error: error instanceof Error ? error.message : 'Failed to fetch empaques',
            },
            false,
            'parametric/fetchEmpaques/error'
          );
        }
      },

      refetch: async () => {
        await get().fetchAllParametricData();
      },

      clearError: () => {
        set({ error: null }, false, 'parametric/clearError');
      },

      reset: () => {
        set(initialState, false, 'parametric/reset');
      },
    }),
    {
      name: 'parametric-store',
    }
  )
);

// Convenience hooks for individual parametric data
export const useTiposIdentificacion = () => {
  const { tiposIdentificacion, loadingTiposIdentificacion, fetchTiposIdentificacion } = useParametricStore();
  return { data: tiposIdentificacion, loading: loadingTiposIdentificacion, fetch: fetchTiposIdentificacion };
};

export const useGeneros = () => {
  const { generos, loadingGeneros, fetchGeneros } = useParametricStore();
  return { data: generos, loading: loadingGeneros, fetch: fetchGeneros };
};

export const useGrupos = () => {
  const { grupos, loadingGrupos, fetchGrupos } = useParametricStore();
  return { data: grupos, loading: loadingGrupos, fetch: fetchGrupos };
};
export const useUnidadesMedida = () => {
  const { unidadesMedida, loadingUnidadesMedida, fetchUnidadesMedida } = useParametricStore();
  return { data: unidadesMedida, loading: loadingUnidadesMedida, fetch: fetchUnidadesMedida };
};

export const useProcedencias = () => {
  const { procedencias, loadingProcedencias, fetchProcedencias } = useParametricStore();
  return { data: procedencias, loading: loadingProcedencias, fetch: fetchProcedencias };
};

export const useOrigenProductos = () => {
  const { origenProductos, loadingOrigenProductos, fetchOrigenProductos } = useParametricStore();
  return { data: origenProductos, loading: loadingOrigenProductos, fetch: fetchOrigenProductos };
};

export const useEmpaques = () => {
  const { empaques, loadingEmpaques, fetchEmpaques } = useParametricStore();
  return { data: empaques, loading: loadingEmpaques, fetch: fetchEmpaques };
};

// Backward compatibility hook to match ParametricContext API
export const useParametric = () => {
  const {
    tiposIdentificacion,
    generos,
    grupos,
    loading,
    error,
    refetch
  } = useParametricStore();

  const data: ParametricData = {
    tiposIdentificacion,
    generos,
    grupos,
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
};